// hooks/useResultsData.js
import {useState, useEffect} from 'react';
import {OrganizationApiService} from '../services/api/organizationApi';
import {ORGANIZATION_TYPES} from '../utils/constants';

export const useResultsData = (organizationType, apiClients) => {
  const [data, setData] = useState({
    results: null,
    demographics: null,
    symptoms: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setData(prev => ({...prev, loading: true, error: null}));

        // Fetch demographics
        const demographics = await fetchDemographics(apiClients.questionnaireApi);

        // Fetch symptoms if available
        const symptoms = await fetchSymptoms(apiClients.assessmentApi, organizationType);

        // Fetch results based on organization
        const apiService = new OrganizationApiService(
          organizationType,
          organizationType === ORGANIZATION_TYPES.DEFAULT ? apiClients.nuralogixApi : apiClients.clientApi,
        );

        const results = await apiService.fetchResults();

        setData({
          results,
          demographics,
          symptoms,
          loading: false,
          error: null,
        });
      } catch (error) {
        console.error('Error fetching results data:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: error.message,
        }));
      }
    };

    if (apiClients) {
      fetchAllData();
    }
  }, [organizationType, apiClients]);

  return data;
};

async function fetchDemographics(questionnaireApi) {
  if (!questionnaireApi) return null;

  try {
    const response = await questionnaireApi.getQuestionnaire();
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching demographics:', error);
  }
  return null;
}

async function fetchSymptoms(assessmentApi, organizationType) {
  // Check localStorage for organization-specific symptoms
  if (organizationType !== ORGANIZATION_TYPES.DEFAULT) {
    const storedConditions = localStorage.getItem('conditions');
    if (storedConditions) {
      const conditions = JSON.parse(storedConditions);
      return processSymptoms(conditions);
    }
  }

  // Fetch from API for default organization
  if (!assessmentApi) return null;

  try {
    const assessment = await assessmentApi.getLatestAssessment();
    if (assessment.status === 200 && assessment.data.symptoms_description) {
      const results = await assessmentApi.getResultsAssessment(assessment.data.id);
      if (results.status === 200) {
        return processSymptoms(results.data.conditions);
      }
    }
  } catch (error) {
    console.error('Error fetching symptoms:', error);
  }
  return null;
}

function processSymptoms(conditions) {
  if (!conditions || !Array.isArray(conditions)) return null;

  const symptomsList = conditions.map((condition, index) => ({
    id: index,
    name: condition.name,
    probability: condition.probability,
    symptomColour: '#35C6C9', // You can make this dynamic
  }));

  // Sort by probability and get top 3
  symptomsList.sort((a, b) => b.probability - a.probability);
  return symptomsList.slice(0, 3);
}
